/**
 * Pantry-first dinners for the weekly email.
 *
 * The taste score already counts pantry ingredients (+8 each, up to five), but
 * against a whole library that is a nudge, not a plan: on a real household with
 * 23 active items it moved pantry use from 0.10 to 0.28 ingredients per
 * proposed meal — nobody would notice. So the email reserves its first
 * `PANTRY_FIRST_DINNERS` dinners for recipes that cook what is already on the
 * shelf, the items that run out soonest first, and says which ones it used.
 *
 * Only perishables count: salt, oil and flour match every recipe and use up
 * nothing. The reservation never overrides a rule — a pantry dinner is picked
 * from the pool that already honours the cook's hard rules, the schedule, the
 * week's balance and the same-meal windows. Pure and server-safe.
 */
import { shelfMatcher } from './pantryMatch';
import { addDays } from '../utils/localDate';

/** How many dinners of a proposed week are held for the pantry. */
export const PANTRY_FIRST_DINNERS = 2;

/**
 * An item "runs out soon" when its date falls by this many days into the
 * proposed week (Monday + 3 = Thursday). Earlier dinners are filled first, so
 * those are the ones the reservation lands on.
 */
export const EXPIRING_WITHIN_DAYS = 3;

/** Shelf categories worth cooking before they spoil (as the pantry's « À utiliser »). */
export const PERISHABLE_CATEGORIES = new Set(['Meat', 'Plant-Based Protein', 'Vegetables', 'Bread/Grains', 'Fruits', 'Dairy', 'Leftovers']);

/** One pantry item as the email reads it off the inventory document. */
export interface ShelfItem {
  /** Lower-cased, trimmed. */
  name: string;
  /** Expiry as YYYY-MM-DD, when the pantry knows one. */
  expiresOn?: string;
  /** Carried for the matcher: the row's stored identity (#601), and 'leftover' (#663). */
  canonicalId?: string;
  source?: string;
}

/** A pantry row's expiry as YYYY-MM-DD, or undefined when it holds none. */
export function expiryIso(e: { year?: unknown; month?: unknown; day?: unknown } | null | undefined): string | undefined {
  return e && Number.isInteger(e.year) && Number.isInteger(e.month) && Number.isInteger(e.day)
    ? `${e.year}-${String(e.month).padStart(2, '0')}-${String(e.day).padStart(2, '0')}`
    : undefined;
}

/**
 * The perishables on a household's shelf, from raw inventory documents.
 * Active items only (a missing status is active, as in the app), nothing in
 * the dry pantry or the freezer, nothing already more than two days past its
 * date — that is the pantry's "review" pile, not dinner.
 */
export function perishableShelf(docs: Array<Record<string, any> | undefined>, today: string): ShelfItem[] {
  const out: ShelfItem[] = [];
  const seen = new Set<string>();
  for (const d of docs) {
    if (!d || (d.status !== undefined && d.status !== 'active')) continue;
    const name = typeof d.name === 'string' ? d.name.trim().toLowerCase() : '';
    if (name.length < 3 || seen.has(name)) continue;
    if (d.location === 'pantry' || d.location === 'freezer') continue;
    if (!PERISHABLE_CATEGORIES.has(d.category)) continue;
    const expiresOn = expiryIso(d.expiryDate);
    if (expiresOn && expiresOn < addDays(today, -2)) continue;
    seen.add(name);
    out.push({
      name,
      expiresOn,
      ...(typeof d.canonicalId === 'string' ? { canonicalId: d.canonicalId } : {}),
      ...(typeof d.source === 'string' ? { source: d.source } : {}),
    });
  }
  return out;
}

/** What a recipe would take off the shelf. */
export interface PantryUse {
  /** Shelf item names the recipe uses, soonest to expire first. */
  items: string[];
  /** How many of them run out within `EXPIRING_WITHIN_DAYS` of the week's start. */
  expiring: number;
}

/**
 * The shelf items one recipe uses, matched the way the recipe screen, the
 * fans' « Utilise votre frigo » and the taste score match (`pantryMatch.ts`), so
 * all of them agree. `skip` holds items another pantry dinner has already
 * claimed.
 */
export function pantryUseOf(
  recipe: { ingredients?: string[] },
  shelf: ShelfItem[],
  weekStart: string,
  skip: ReadonlySet<string> = new Set(),
): PantryUse {
  const soon = addDays(weekStart, EXPIRING_WITHIN_DAYS);
  const used = shelfMatcher(shelf).itemsUsedBy(recipe).filter((item) => !skip.has(item.name));
  used.sort((a, b) => (a.expiresOn ?? '9999').localeCompare(b.expiresOn ?? '9999'));
  return {
    items: used.map((i) => i.name),
    expiring: used.filter((i) => i.expiresOn !== undefined && i.expiresOn <= soon).length,
  };
}

/**
 * The recipes a pantry dinner may be picked from: those using at least one
 * unclaimed perishable, best first — the most items running out soon, then
 * the most items, then the cook's own score. Empty when nothing on the shelf
 * is used, and the slot is then filled the ordinary way.
 */
export function rankForPantry<T extends { ingredients?: string[] }>(
  pool: T[],
  shelf: ShelfItem[],
  weekStart: string,
  claimed: ReadonlySet<string>,
  scoreOf: (r: T) => number,
): Array<{ recipe: T; use: PantryUse }> {
  if (shelf.length === 0) return [];
  return pool
    .map((recipe) => ({ recipe, use: pantryUseOf(recipe, shelf, weekStart, claimed) }))
    .filter((c) => c.use.items.length > 0)
    .sort((a, b) =>
      b.use.expiring - a.use.expiring
      || b.use.items.length - a.use.items.length
      || scoreOf(b.recipe) - scoreOf(a.recipe));
}
